/**
 * rosterSisName — recovering the name a synced roster carries in the SIS.
 *
 * Rosters arrive from Clever/ClassLink under the name the SIS gives them, and
 * schools rename most of them every year. A rename writes `nickname` and leaves
 * `name` untouched, so the original is still on the wire — it was just never
 * shown anywhere, and support was telling people to open the network tab to
 * read it back (ENG-8735).
 *
 * This resolves the "worth showing" case: a provider-owned roster whose display
 * name no longer matches what the provider calls it.
 */

/** The roster fields this needs, from either the raw API shape or a view model. */
export interface RosterSisNameSource {
  name?: string | null;
  nickname?: string | null;
  provider_name?: string | null;
  providerName?: string | null;
}

export interface RosterSisName {
  /** The roster's name in the SIS, i.e. the pre-rename `name`. */
  originalName: string;
  /** Provider display name, for copy: 'Clever' or 'ClassLink'. */
  providerLabel: string;
}

/** Provider display names, keyed by the lowercased `provider_name`. */
const PROVIDER_LABELS: Record<string, string> = {
  clever: 'Clever',
  classlink: 'ClassLink',
};

/**
 * Display label for a rostering provider, or null when the value names no
 * provider we sync with. Used for the "Name in Clever" column heading, which is
 * keyed off the SITE's provider rather than any one roster's.
 */
export function rosterProviderLabel(
  providerName: string | null | undefined,
): string | null {
  return PROVIDER_LABELS[(providerName ?? '').toString().trim().toLowerCase()] ?? null;
}

/**
 * The SIS name worth surfacing for a roster, or null when there is nothing to
 * say — which is the common case, and deliberately silent:
 *
 * - manually created rosters have no SIS name to match back to;
 * - an unrenamed synced roster already shows its SIS name;
 * - a nickname equal to the name (bar case and surrounding space) is a rename
 *   that changed nothing, and repeating it back reads as a bug.
 */
export function resolveRosterSisName(
  roster: RosterSisNameSource | null | undefined,
): RosterSisName | null {
  if (!roster) return null;

  const provider = (roster.provider_name ?? roster.providerName ?? '')
    .toString()
    .trim()
    .toLowerCase();
  const providerLabel = PROVIDER_LABELS[provider];
  if (!providerLabel) return null;

  const originalName = (roster.name ?? '').trim();
  const nickname = (roster.nickname ?? '').trim();
  if (!originalName || !nickname) return null;
  if (nickname.toLowerCase() === originalName.toLowerCase()) return null;

  return { originalName, providerLabel };
}
